import cn from 'classnames';

import Heading from 'components/heading/Heading';
import ContentArea, { ContentAreaTheme } from 'components/content-area/ContentArea';
import Icon, { type IconName } from 'components/icon/Icon';
import SectionTitle from 'components/section-title/SectionTitle';

import { IPortfolioContent, IContentAreaItem, ILink } from 'source/NFR.types';

const columns = (blocks: IContentAreaItem[], singleColumn?: boolean): ContentAreaTheme | undefined => {
  if (singleColumn) return 'singleColumn';
  if (blocks.length >= 3) return 'threeColumns';
  if (blocks.length === 2) return 'twoColumns';
  return undefined;
};

type PropsReturnType = [Record<string, unknown> & { decreaseTopMargin?: boolean }, { name: IconName | '' }];

const props = (blocks: IContentAreaItem[], singleColumn?: boolean): PropsReturnType => {
  switch (blocks[0].componentName) {
    case 'LinkLists':
      return [
        {
          additionalComponentProps: {
            LinkLists: {
              singleColumn
            }
          },
          decreaseTopMargin: true
        },
        { name: 'Link_2022' }
      ];
    case 'LinkWithText':
      return [
        {
          theme: [columns(blocks, singleColumn), 'portfolio'],
          decreaseTopMargin: true
        },
        { name: 'BookOpen_2022' }
      ];
    case 'ContactList':
      return [{}, { name: 'Envelope_2022' }];
    default:
      return [{}, { name: '' }];
  }
};

interface IPortfolioContentProps extends Omit<IPortfolioContent, 'isInsidePriorityBlock' | 'singleColumn'> {
  isInsidePriorityBlock?: IPortfolioContent['isInsidePriorityBlock'];
  singleColumn?: IPortfolioContent['singleColumn'];
}

const PortfolioContent: React.FC<React.PropsWithChildren<IPortfolioContentProps>> = ({
  isInsidePriorityBlock = false,
  title,
  text,
  content,
  singleColumn = false,
  children,
  emptyListText = '',
  sectionTitleLinks = []
}) => {
  const [extraProps, icon] =
    content?.blocks && content.blocks.length
      ? props(content.blocks, singleColumn)
      : ([{}, { name: '' }] as PropsReturnType);

  return (
    <div
      className={cn('portfolio-content', {
        'portfolio-content--inside-priority': isInsidePriorityBlock
      })}
    >
      {isInsidePriorityBlock && <Icon className="portfolio-content--icon" {...icon} />}
      <div className="portfolio-content--content">
        <div
          className={cn(
            'portfolio-content--heading',
            !singleColumn && sectionTitleLinks && 'portfolio-content--heading--with-section-title'
          )}
        >
          {title && (
            <Heading level={2} className="portfolio-content--title">
              {title}
            </Heading>
          )}
          <div className="section--title-links">
            {!singleColumn &&
              sectionTitleLinks?.map((x, index) => (
                <SectionTitle key={'sectTitle #' + index} sectionLink={x as ILink} />
              ))}
            {text && <p className="portfolio-content--text">{text}</p>}
          </div>
        </div>
        {emptyListText && <p>{emptyListText}</p>}
        {content && content.blocks && (
          <ContentArea
            className={cn('portfolio-content--blocks', {
              'portfolio-content--blocks--top-content-is-text': extraProps.decreaseTopMargin
            })}
            enableElementSizing={true}
            {...extraProps}
            {...content}
          />
        )}
        {children}
        {sectionTitleLinks &&
          singleColumn &&
          sectionTitleLinks?.map((x, index) => <SectionTitle key={'sectTitle #' + index} sectionLink={x as ILink} />)}
      </div>
    </div>
  );
};

PortfolioContent.displayName = 'PortfolioContent';

export default PortfolioContent;
